// Background health probe. Polls the gateway on a fixed interval so we
// detect a deployment even when the user is sitting on a quiet screen
// that issues no other API calls. When the gateway returns 502/503/504
// we fire the existing `maintenance_mode` event — App.js takes the user
// to the maintenance page and signs them out.
import { useEffect, useRef } from 'react';
import SbomApiInstance from '../services/HelperMethod/SbomApiIntercepter';
import settings from '../services/config/settings';

const POLL_INTERVAL_MS = 20_000;
const PROBE_TIMEOUT_MS = 10_000;
// Trip maintenance on the first failed probe so the page reacts the moment
// the gateway goes down. Trade-off: a one-off transient network blip will
// also log the user out — acceptable here because the recovery poll in
// App.js re-probes every 30s and clears maintenance as soon as the server
// answers again, so a real blip just means a brief detour through the
// maintenance page.
const FAIL_STREAK_TO_TRIP = 1;

// `/health/` sits at the API host root, not under `/nmt/api/v1/`, so derive
// from the API URL's origin instead of concatenating onto the full base.
// Use the env-resolved base from settings (prod → prodapi, stag → stagapi, etc.)
// rather than process.env directly, which can lag behind on built bundles.
function deriveHealthUrl() {
    const base = settings?.api?.url || process.env.REACT_APP_API_URL || '';
    try { return new URL(base).origin + '/health/'; }
    catch (_) { return '/health/'; }
}

function hasSession() {
    try {
        return !!(sessionStorage.getItem('access_token')
            || localStorage.getItem('access_token'));
    } catch (_) { return false; }
}

// Corroborate a bare network error against the app's own static origin.
// The SPA is served from CloudFront, which stays up during an API-only
// deploy but is unreachable when the user's own internet is down. If we
// can still fetch our origin, the internet works and the failure is the
// API (probable deploy → trip). If we can't, the user is offline and we
// must NOT show maintenance. `navigator.onLine` can't tell these apart
// (it's true whenever any network interface exists — WiFi with no
// upstream, captive portal, VPN, DNS hiccup), so we probe for real.
async function appOriginReachable() {
    try {
        await fetch(`${window.location.origin}/?_hc=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
            // Same-origin request; keep it opaque and header-free so it's
            // as cheap and CORS-proof as possible.
            mode: 'no-cors',
        });
        return true;
    } catch (_) {
        return false;
    }
}

export default function HealthCheckMonitor() {
    const timerRef = useRef(null);
    const failStreakRef = useRef(0);

    useEffect(() => {
        const url = deriveHealthUrl();

        const trip = () => {
            failStreakRef.current = 0;
            window.dispatchEvent(new Event('maintenance_mode'));
        };

        const probe = async () => {
            // Skip while nobody is logged in — the maintenance flow only
            // matters once a user is authenticated.
            if (!hasSession()) return;
            try {
                await SbomApiInstance.get(url, { timeout: PROBE_TIMEOUT_MS });
                failStreakRef.current = 0;
            } catch (err) {
                const status = err?.response?.status;
                // Definite deploy signal — gateway returned a 5xx that the
                // browser handed us. Trip immediately.
                if (status === 502 || status === 503 || status === 504) {
                    trip();
                    return;
                }
                // No response on a request the browser claims should have
                // worked (online, not aborted). Most common cause: nginx's
                // 5xx error page is missing `Access-Control-Allow-Origin`,
                // so the browser strips the response from JS and we can't
                // read the status. But this ALSO fires when the user's own
                // internet is down (WiFi with no upstream, captive portal,
                // DNS hiccup) — and `navigator.onLine` stays true in those
                // cases, so it can't be trusted on its own. Corroborate
                // against our static origin: only trip if the internet
                // actually works and it's just the API that's unreachable.
                const online = typeof navigator === 'undefined'
                    ? true : navigator.onLine !== false;
                const aborted = err?.code === 'ERR_CANCELED'
                    || err?.name === 'CanceledError';
                if (!err?.response && online && !aborted) {
                    if (!(await appOriginReachable())) {
                        // User's own connection is down — not a deploy.
                        failStreakRef.current = 0;
                        return;
                    }
                    failStreakRef.current += 1;
                    if (failStreakRef.current >= FAIL_STREAK_TO_TRIP) trip();
                    return;
                }
                // Any other error (4xx, offline, abort) — reset streak so
                // the trip only fires on a sustained server-side outage.
                failStreakRef.current = 0;
            }
        };

        // Fire one probe up-front so a deploy that started before the user
        // landed is caught inside a second instead of waiting a full interval.
        probe();
        timerRef.current = setInterval(probe, POLL_INTERVAL_MS);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    return null;
}
